import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, MapPin, Navigation, Clock, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { SelfieCheckInDialog } from "./SelfieCheckInDialog";
import { useLocationPermission } from "@/hooks/useLocationPermission";

export type CheckInReason = 
  | "too_far" 
  | "location_denied" 
  | "event_ended" 
  | "event_not_started" 
  | "no_location_data"
  | "location_loading"
  | "default";

interface CheckInOptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  eventTitle: string;
  reason?: CheckInReason;
  distance?: number;
  eventStartTime?: Date;
}

const getReasonConfig = (reason: CheckInReason, distance?: number, eventStartTime?: Date) => {
  switch (reason) {
    case "too_far":
      return {
        icon: Navigation,
        iconColor: "text-amber-500",
        title: "You're Not at the Event Yet",
        description: distance 
          ? `You're ${distance.toFixed(1)} miles away. Check in when you arrive, or submit a selfie now.`
          : "You're too far from the event location. Submit a selfie to verify attendance.",
        showLocationOption: true,
        locationOptionText: "I'm Here Now (Use Location)",
      };
    case "location_denied":
      return {
        icon: AlertTriangle,
        iconColor: "text-destructive",
        title: "Location Access Disabled",
        description: "Enable location in settings for automatic check-in, or submit a selfie to verify attendance.",
        showLocationOption: true,
        locationOptionText: "Enable Location in Settings",
      };
    case "event_ended":
      return {
        icon: Clock,
        iconColor: "text-muted-foreground",
        title: "Submit Late Check-In",
        description: "The check-in window has closed. Submit a selfie for manual review within 7 days.",
        showLocationOption: false,
        locationOptionText: "",
      };
    case "event_not_started":
      return {
        icon: Clock,
        iconColor: "text-blue-500",
        title: "Check-In Opens Soon",
        description: eventStartTime 
          ? `Check-in opens 1 hour before the event (${eventStartTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}). You can submit a selfie now or wait.`
          : "Check-in opens 1 hour before the event begins.",
        showLocationOption: false,
        locationOptionText: "",
      };
    case "no_location_data":
      return {
        icon: MapPin,
        iconColor: "text-muted-foreground",
        title: "Location Not Available",
        description: "We couldn't determine your location. Try again or submit a selfie.",
        showLocationOption: true,
        locationOptionText: "Try Again",
      };
    default:
      return {
        icon: MapPin,
        iconColor: "text-primary",
        title: "Choose Check-In Method",
        description: "Select how you'd like to verify your attendance at this event.",
        showLocationOption: true,
        locationOptionText: "Enable Location Services",
      };
  }
};

export const CheckInOptionsDialog = ({ 
  open, 
  onOpenChange, 
  eventId, 
  eventTitle,
  reason = "default",
  distance,
  eventStartTime,
}: CheckInOptionsDialogProps) => {
  const [selfieDialogOpen, setSelfieDialogOpen] = useState(false);
  const { openAppSettings, requestPermission, getCurrentPosition, isDenied } = useLocationPermission();

  const config = getReasonConfig(reason, distance, eventStartTime);
  const Icon = config.icon;

  const handleLocationAction = async () => {
    if (reason === "location_denied" || isDenied) {
      await openAppSettings();
      onOpenChange(false);
      return;
    }
    
    // Try getting position directly first - if permission is granted, this will work
    // This avoids showing the explainer when permission is already granted
    const position = await getCurrentPosition();
    if (position) {
      // Success! Location is working, just close dialog
      // The parent component will refresh and see the location
      onOpenChange(false);
      return;
    }
    
    // If we couldn't get position, request permission (which shows explainer if needed)
    await requestPermission();
    onOpenChange(false);
  };

  const handleSelfieCheckIn = () => {
    onOpenChange(false);
    setSelfieDialogOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="flex justify-center mb-3">
              <div className={`p-3 rounded-full bg-muted ${config.iconColor}`}>
                <Icon className="h-6 w-6" />
              </div>
            </div>
            <DialogTitle>{config.title}</DialogTitle>
            <DialogDescription className="text-center">
              {config.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 pt-4">
            <Button
              onClick={handleSelfieCheckIn}
              className="w-full h-auto py-4 flex-col gap-2"
              size="lg"
            >
              <Camera className="h-6 w-6" />
              <div className="text-center">
                <div className="font-semibold">
                  {reason === "event_ended" ? "Submit Late Selfie" : "Check In with Selfie"}
                </div>
                <div className="text-xs opacity-90 font-normal">
                  {reason === "event_ended" 
                    ? "Your selfie will be reviewed by our team"
                    : "Take a selfie at the event for verification"
                  }
                </div>
              </div>
            </Button>

            {config.showLocationOption && (
              <Button
                onClick={handleLocationAction}
                variant="outline"
                className="w-full h-auto py-4 flex-col gap-2"
                size="lg"
              >
                <Navigation className="h-6 w-6" />
                <div className="text-center">
                  <div className="font-semibold">{config.locationOptionText}</div>
                  <div className="text-xs opacity-70 font-normal">
                    {reason === "location_denied" 
                      ? "Opens your device settings"
                      : "Automatically check in when near the event"
                    }
                  </div>
                </div>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SelfieCheckInDialog
        open={selfieDialogOpen}
        onOpenChange={setSelfieDialogOpen}
        eventId={eventId}
        eventTitle={eventTitle}
        reason={reason}
        distance={distance}
      />
    </>
  );
};
